/**
 * Manufacturing Details Form Component
 * Form for entering manufacturing and batch-specific information
 */

import { Input } from './ui';
import { ManufacturingDetails } from '../../../backend/src/types/enhanced-product-info.types';

interface ManufacturingDetailsFormProps {
  manufacturingDetails: Partial<ManufacturingDetails>;
  onChange: (field: keyof ManufacturingDetails, value: any) => void;
  disabled?: boolean;
  errors: { [key: string]: string };
}

export default function ManufacturingDetailsForm({
  manufacturingDetails,
  onChange,
  disabled = false
}: ManufacturingDetailsFormProps) {

  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return '';
    return date instanceof Date ? date.toISOString().split('T')[0] : '';
  };

  const handleDateChange = (field: keyof ManufacturingDetails, value: string) => {
    onChange(field, value ? new Date(value) : undefined);
  };

  const handleNumberChange = (field: keyof ManufacturingDetails, value: string) => {
    if (value === '') {
      onChange(field, undefined);
      return;
    }
    const parsed = Number(value);
    onChange(field, Number.isFinite(parsed) ? parsed : undefined);
  };

  return (
    <div>
      <h4 className="text-md font-medium text-gray-900 mb-4">Manufacturing Details</h4>
      <div className="space-y-4">
        {/* Manufacturing Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manufacturing Date
            </label>
            <Input
              type="date"
              value={formatDateForInput(manufacturingDetails.manufacturingDate)}
              onChange={(e) => handleDateChange('manufacturingDate', e.target.value)}
              disabled={disabled}
              max={new Date().toISOString().split('T')[0]} // Can't be in the future
            />
            <p className="text-xs text-gray-500 mt-1">
              Date when this batch was manufactured
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Date
            </label>
            <Input
              type="date"
              value={formatDateForInput(manufacturingDetails.expiryDate)}
              onChange={(e) => handleDateChange('expiryDate', e.target.value)}
              disabled={disabled}
              min={new Date().toISOString().split('T')[0]} // Should be in the future
            />
            <p className="text-xs text-gray-500 mt-1">
              Best before or expiry date
            </p>
          </div>
        </div>

        {/* Batch Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Internal Batch Number
          </label>
          <Input
            value={manufacturingDetails.batchNumber || ''}
            onChange={(e) => onChange('batchNumber', e.target.value)}
            placeholder="e.g., LOT2024001, B240315"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500 mt-1">
            Your internal batch or lot number for tracking purposes
          </p>
        </div>

        {/* MRP */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            MRP (INR)
          </label>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={manufacturingDetails.mrp ?? ''}
            onChange={(e) => handleNumberChange('mrp', e.target.value)}
            placeholder="e.g., 199.00"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum retail price for this product
          </p>
        </div>

        {/* Facility Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Manufacturing Facility
          </label>
          <Input
            value={manufacturingDetails.facilityLocation || ''}
            onChange={(e) => onChange('facilityLocation', e.target.value)}
            placeholder="e.g., Mumbai Plant, Facility A, Gujarat Unit"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500 mt-1">
            Location or name of the manufacturing facility
          </p>
        </div>

        {/* Date Validation Warning */}
        {manufacturingDetails.manufacturingDate && manufacturingDetails.expiryDate && 
         manufacturingDetails.manufacturingDate >= manufacturingDetails.expiryDate && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-sm text-amber-800">
                Expiry date should be after the manufacturing date
              </p>
            </div>
          </div>
        )}

        {/* Helper Text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            <strong>Note:</strong> Manufacturing details help with traceability and quality control. 
            This information may be displayed to consumers for transparency.
          </p>
        </div>
      </div>
    </div>
  );
}
